import { useState } from "react"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import { useAppDispatch } from "../../redux/store"
import {
  clearQrId,
  setOpenHash,
} from "../../redux/slices/events/eventsSlice"
import { IEvent } from "../../components/Events/types"
import { EventBucket } from "./useEventGroups"
import styles from "./EventsPage.module.scss"

interface EventRowProps {
  event: IEvent
  bucket: EventBucket
  // Toggled by the page when the active sort is by creation date. We
  // surface `Created MMM D` inline on the meta row so the ordering is
  // legible without the user having to remember which sort they picked.
  // When sorting by event date we hide it — the event date is already
  // shown prominently just to its left.
  showCreatedDate?: boolean
  onCopyLink: (url: string) => void
}

// Produce compact initials for the avatar fallback. Skips "Copy of" prefix
// so duplicates still read cleanly — "Copy of Wedding" → "W", not "CO".
const getInitials = (name: string): string => {
  const cleaned = name.replace(/^copy of\s+/i, "").trim()
  if (!cleaned) return "·"
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

// Relative-time phrasing tuned to event-organizer mental model:
//   • same-day events dominate, so "Today", "Tomorrow", "Yesterday" win
//   • within a week uses weekday names (humans think in weekdays near-term)
//   • beyond that falls back to moment's "in X days / X days ago"
const formatRelative = (start: moment.Moment | null, bucket: EventBucket): string => {
  if (!start) {
    if (bucket === "draft") return "Not scheduled"
    return "Unscheduled"
  }
  const now = moment()
  const diffDays = start.startOf("day").diff(now.startOf("day"), "days")

  if (diffDays === 0) return "Today"
  if (diffDays === 1) return "Tomorrow"
  if (diffDays === -1) return "Yesterday"
  if (diffDays > 1 && diffDays <= 6) return `In ${diffDays} days`
  if (diffDays < -1 && diffDays >= -6) return `${Math.abs(diffDays)} days ago`
  return start.fromNow()
}

// Absolute date pattern: "Sat, Feb 24 · 7:00 PM" (drop year unless it
// differs from the current year — Stripe never shows redundant year).
const formatAbsolute = (start: moment.Moment | null): string | null => {
  if (!start) return null
  const sameYear = start.year() === moment().year()
  return start.format(sameYear ? "ddd, MMM D · h:mm A" : "MMM D, YYYY · h:mm A")
}

// Icons ---------------------------------------------------------------------

const CopyIcon = ({ done }: { done?: boolean }) => {
  if (done) {
    return (
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
        <path
          d="M3 7.5L6 10.5L11 4.5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <rect
        x="4"
        y="4"
        width="8"
        height="8"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M2 10V3a1 1 0 0 1 1-1h7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M5 3L9 7L5 11"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// ---------------------------------------------------------------------------

// Absolute format for the creation date shown inline when sorting by
// creation date. Year-aware (drops the year in the current year) so
// rows don't get noisier than they have to.
const formatCreatedDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null
  const m = moment(iso)
  if (!m.isValid()) return null
  return m.year() === moment().year()
    ? m.format("MMM D")
    : m.format("MMM D, YYYY")
}

const EventRow = ({
  event,
  bucket,
  showCreatedDate,
  onCopyLink,
}: EventRowProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)

  const start = event.start_datetime ? moment(event.start_datetime) : null
  const relative = formatRelative(start, bucket)
  const absolute = formatAbsolute(start)
  const createdLabel = showCreatedDate
    ? formatCreatedDate(event.event_created_date)
    : null

  const isOpen = event.is_open_event === 1
  const registrationUrl = event.open_event_hash
    ? `app.mazoom.sa/register/${event.open_event_hash}`
    : null

  // Navigate into the event dashboard. Mirrors the behavior of the legacy
  // <Link to={`/${event_id}`}> in EventsItem so existing routing is preserved.
  const openEvent = () => {
    dispatch(clearQrId())
    dispatch(setOpenHash(isOpen ? event.open_event_hash : null))
    navigate(`/${event.event_id}`)
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!registrationUrl) return
    const full = `https://${registrationUrl}`
    navigator.clipboard?.writeText(full).catch(() => {})
    setCopied(true)
    onCopyLink(full)
    window.setTimeout(() => setCopied(false), 1800)
  }

  // Status dot variant mirrors the bucket so visual language matches the
  // group header — blue = upcoming, amber = draft, gray = past.
  const statusClass = [
    styles.rowStatus,
    styles[
      `rowStatus${bucket.charAt(0).toUpperCase()}${bucket.slice(1)}`
    ],
  ].join(" ")

  // Contextual metric chips depend on event type. See `buildMetrics` — we
  // only render non-zero facts, so a row with one declined reads "1
  // declined" instead of "Invited 0 · Accepted 0 · Declined 1".
  const metrics = buildMetrics(event, bucket, isOpen)

  return (
    <div
      className={styles.row}
      onClick={openEvent}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          openEvent()
        }
      }}
    >
      <span className={statusClass} aria-hidden="true" />

      <div className={styles.rowAvatar} aria-hidden="true">
        {event.pattern ? (
          <img src={event.pattern} alt="" />
        ) : (
          <span>{getInitials(event.name)}</span>
        )}
      </div>

      <div className={styles.rowBody}>
        <div className={styles.rowName}>{event.name}</div>
        <div className={styles.rowMeta}>
          {absolute && (
            <>
              <span className={styles.rowMetaItem}>{absolute}</span>
              <span className={styles.rowMetaSep}>·</span>
            </>
          )}
          <span
            className={`${styles.rowMetaItem} ${styles.rowMetaItemRelative}`}
          >
            {relative}
          </span>

          {createdLabel && (
            <>
              <span className={styles.rowMetaSep}>·</span>
              <span className={styles.rowMetaItem}>
                Created {createdLabel}
              </span>
            </>
          )}

          {isOpen && registrationUrl && bucket !== "past" && (
            <>
              <span className={styles.rowMetaSep}>·</span>
              <span
                className={styles.rowLink}
                onClick={(e) => e.stopPropagation()}
                title={`https://${registrationUrl}`}
              >
                {registrationUrl}
              </span>
              <button
                type="button"
                className={`${styles.rowCopyBtn} ${
                  copied ? styles.rowCopyBtnCopied : ""
                }`}
                onClick={handleCopy}
                aria-label={copied ? "Link copied" : "Copy registration link"}
              >
                <CopyIcon done={copied} />
              </button>
            </>
          )}

          {bucket === "draft" && (
            <>
              <span className={styles.rowMetaSep}>·</span>
              <span
                className={`${styles.rowStatusPill} ${styles.rowStatusPillDraft}`}
              >
                Draft
              </span>
            </>
          )}

        </div>
      </div>

      {metrics.visible && (
        <div className={styles.rowMetrics}>
          <div
            className={styles.rowMetricBar}
            role="img"
            aria-label={metrics.caption}
          >
            {metrics.total > 0 &&
              metrics.segments.map((seg) => {
                const pct = (seg.value / metrics.total) * 100
                return (
                  <span
                    key={seg.tone}
                    className={`${styles.rowMetricBarSeg} ${
                      styles[
                        `rowMetricBarSeg${seg.tone[0].toUpperCase()}${seg.tone.slice(1)}`
                      ]
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                )
              })}
          </div>
          <span className={styles.rowMetricCaption}>{metrics.caption}</span>
        </div>
      )}

      <span className={styles.rowArrow} aria-hidden="true">
        <ChevronRightIcon />
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row metrics — the "traffic bar" pattern
// ---------------------------------------------------------------------------
// Every non-draft row shows:
//
//   1. A proportional traffic bar (accepted / declined / awaiting, or
//      a single attended band on past rows). When the row has zero
//      guests the bar is just an empty track — signals "ready to fill"
//      rather than hiding the metrics slot entirely.
//
//   2. A numeric legend underneath. The legend always leads with the
//      guest total (the roster size) so users see the denominator on
//      every row, then appends whichever of accepted / declined /
//      awaiting / attended are non-zero. "Guests" matches the word
//      used on the detail page ("5 of 7 guests") and avoids implying
//      that an invitation has already been sent, which "invited"
//      does. Zero-guest rows collapse to the tiny "0 guests" so the
//      column never goes silent.
//
// Drafts still opt out entirely — their counts aren't meaningful yet —
// but every other bucket renders the full metric region so scanning
// the list always gives the user the numbers they came for.

type MetricTone = "accepted" | "declined" | "awaiting" | "attended"

interface MetricSegment {
  tone: MetricTone
  value: number
  label: string
}

interface RowMetrics {
  visible: boolean
  segments: MetricSegment[]
  total: number
  caption: string
}

const HIDDEN: RowMetrics = {
  visible: false,
  segments: [],
  total: 0,
  caption: "",
}

interface LegendPart {
  value: number
  label: string
  // When true, the part always renders — even at zero. We use this for
  // the `invited` total so every row shows its denominator.
  alwaysShow?: boolean
}

// Drop the trailing "s" from a plural noun when the count is exactly 1
// so "1 guests" becomes "1 guest". The other labels used in this file
// ("accepted", "declined", "awaiting", "attended") are past-tense verbs
// or adjectives that already read correctly with a count of one, so
// this is effectively just for "guests" — but generalizing keeps the
// function honest if new nouns are added later.
const pluralize = (count: number, word: string): string =>
  count === 1 && word.endsWith("s") ? word.slice(0, -1) : word

const formatLegend = (parts: LegendPart[]): string =>
  parts
    .filter((p) => p.alwaysShow || p.value > 0)
    .map((p) => `${p.value} ${pluralize(p.value, p.label)}`)
    .join(" · ")

// Invited total — must mirror Overview's computation in
// src/pages/EventDashboardV2/components/Overview.tsx so the list and the
// detail page never disagree. The backend keeps `count_all_contacts` as a
// separate summary field that can read 0 even when guests exist (it tracks
// only a subset of contacts), so the dashboard sums the WhatsApp status
// counters instead. We do the same here, then fall back to count_all_contacts
// for non-WhatsApp flows, and finally clamp to accepted+declined so a row
// that's already received responses can never report fewer invited than
// respondents (which would make the traffic-bar math negative).
const computeInvited = (event: IEvent): number => {
  const waUnsent = event.count_whatsapp_invitations_unsent ?? 0
  const waSent = event.count_whatsapp_invitations_sent ?? 0
  const waDelivered = event.count_whatsapp_invitations_delivered ?? 0
  const waReceived = event.count_whatsapp_invitations_received ?? 0
  const waRead = event.count_whatsapp_invitations_read ?? 0
  const waFailed = event.count_whatsapp_invitations_failed ?? 0
  const whatsappTotal =
    waUnsent + waSent + waDelivered + waReceived + waRead + waFailed

  const accepted = event.count_contacts_going ?? 0
  const declined = event.count_contacts_decline ?? 0

  return Math.max(
    whatsappTotal || event.count_all_contacts || 0,
    accepted + declined,
  )
}

const buildMetrics = (
  event: IEvent,
  bucket: EventBucket,
  _isOpen: boolean,
): RowMetrics => {
  if (bucket === "draft") return HIDDEN

  const invited = computeInvited(event)
  const accepted = event.count_contacts_going ?? 0
  const declined = event.count_contacts_decline ?? 0

  if (bucket === "past") {
    // Past events collapse to one question: who actually attended. The
    // bar has a single green segment; the rest of the track reads as
    // "didn't show". The legend always leads with invited and appends
    // attended when there's any, so even zero-attendance rows still
    // carry the numeric signal.
    const segments: MetricSegment[] =
      accepted > 0
        ? [{ tone: "attended", value: accepted, label: "attended" }]
        : []
    return {
      visible: true,
      segments,
      total: invited,
      caption: formatLegend([
        { value: invited, label: "guests", alwaysShow: true },
        { value: accepted, label: "attended" },
      ]),
    }
  }

  // "Awaiting" must match Overview's "Awaiting reply" definition so
  // the list and detail pages agree: a guest can only be awaiting a
  // reply after their invitation has been delivered. Undelivered
  // invitations (in progress, unreachable, failed) occupy the rest of
  // the bar as empty track — same visual logic as the detail page's
  // "Invitations delivered" card, which reads e.g. 71% when 5 of 7
  // invitations have landed.
  const waDelivered = event.count_whatsapp_invitations_delivered ?? 0
  const waReceived = event.count_whatsapp_invitations_received ?? 0
  const waRead = event.count_whatsapp_invitations_read ?? 0
  const delivered = waDelivered + waReceived + waRead

  // For non-WhatsApp flows the delivered counters may be zero even
  // though guests have responded (responses came via a direct link or
  // manual entry). Treat any respondent as trivially "reached" so the
  // math can't go negative and the caption stays truthful.
  const effectiveDelivered = Math.max(delivered, accepted + declined)
  const awaiting = Math.max(0, effectiveDelivered - accepted - declined)

  const segments: MetricSegment[] = []
  if (accepted > 0) {
    segments.push({ tone: "accepted", value: accepted, label: "accepted" })
  }
  if (declined > 0) {
    segments.push({ tone: "declined", value: declined, label: "declined" })
  }
  if (awaiting > 0) {
    segments.push({ tone: "awaiting", value: awaiting, label: "awaiting" })
  }

  return {
    visible: true,
    segments,
    total: invited,
    caption: formatLegend([
      { value: invited, label: "guests", alwaysShow: true },
      { value: accepted, label: "accepted" },
      { value: declined, label: "declined" },
      { value: awaiting, label: "awaiting" },
    ]),
  }
}

export default EventRow
